import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import { mapKeys, mapValues, pickBy, omitBy, cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'

export default {
    computed: {
        ...mapGetters('designerTiers', [
            'getTierId'
        ]),
        routing () {
            const vue = this

            return {
                router: historyRouter({
                    writeDelay: 0,
                    cleanUrlOnDispose: false,
                    createURL({ qsModule, routeState, location }) {
                        const { origin, pathname, hash } = location
                        let path = pathname
                        let queryObj = cloneDeep(routeState)

                        if (routeState.search.category && ['products', 'categories'].includes(vue.$route.name)) {
                            path = `/categories/${routeState.search.category[0]}`
                            delete queryObj.search.category
                        } else {
                            path = vue.$route.path
                        }

                        const queryString = qsModule.stringify(queryObj)
                        const queryStringConnector = !!queryString ? '?' : ''

                        return `${origin}${path}${queryStringConnector}${queryString}${hash}`
                    }
                }),
                stateMapping: this.stateMapping
            }
        },
        stateMapping () {
            return {
                stateToRoute: uiState => this.getStateToRoute(uiState),
                routeToState: routeState => this.getRouteToState(routeState)
            }
        },
        sortParamIndexMapping () {
            const designerTierId = this.getTierId > 0 ? `_designer_tier${this.getTierId}` : ''

            return [
                { param: 'recommended', index: this.indexName },
                { param: 'new_arrival', index: `${this.indexName}_created_at_timestamp_desc` },
                { param: 'price_asc', index: `${this.indexName}${designerTierId}_price_asc` },
                { param: 'price_desc', index: `${this.indexName}${designerTierId}_price_desc` },
                { param: 'name_asc', index: `${this.indexName}_proper_name_asc` },
                { param: 'name_desc', index: `${this.indexName}_proper_name_desc` }
            ]
        },
        toggleAttributes () {
            return this.getAttributesFor('checkbox')
        },
        multiselectionAttributes () {
            return this.getAttributesFor('multiselection')
        },
        rangeAttributes () {
            return this.getAttributesFor('range')
        },
        hierarchicalMenuAttributes () {
            return this.getAttributesFor('multiselection_tree')
        },
        labelsMappings () {
            const designerTierId = this.getTierId > 0 ? `designer_tier${this.getTierId}_` : ''

            return [
                { filter: 'on_sale', label: 'sale' },
                { filter: 'supplier.name', label: 'brand' },
                { filter: `${designerTierId}price`, label: 'price' }
            ]
        }
    },
    methods: {
        getStateToRoute (uiState) {
            const indexUiState = uiState[this.indexName]
            const keyword = this.getStateToRouteKeywordFilter(indexUiState)
            const order = this.getStateToRouteOrder(indexUiState)
            const checkboxFilters = this.getStateToRouteCheckboxFilters(indexUiState)
            const multiselectionFilters = this.getStateToRouteMultiselectionFilters(indexUiState)
            const multiselectionTreeFilters = this.getStateToRouteMultiselectionTreeFilters(indexUiState)
            const ranges = this.getStateToRouteRangesFilters(indexUiState)
            const page = this.getStateToRoutePage(indexUiState)

            return pickBy({
                order,
                page,
                search: pickBy({
                    keyword,
                    ...checkboxFilters,
                    ...multiselectionFilters,
                    ...omitBy(multiselectionTreeFilters, (v, k) => {
                        return k === 'category' && this.$route.name === 'categories' && v[0] === this.$route.params.name
                    }),
                    ...ranges
                })
            })
        },
        getRouteToState (routeState) {
            let query = this.getRouteToStateQuery(routeState)
            let sortBy = this.getRouteToStateSortBy(routeState)
            let toggle = this.getRouteToStateToggle(routeState)
            let refinementList = this.getRouteToStateRefinementList(routeState)
            let range = this.getRouteToStateRange(routeState)
            let page = this.getRouteToStatePage(routeState)
            let hierarchicalMenu = this.getRouteToStateHierarchicalMenu(routeState)

            return pickBy({
                [this.indexName]: pickBy({
                    query,
                    sortBy,
                    toggle,
                    refinementList,
                    range,
                    page,
                    hierarchicalMenu
                })
            })
        },
        getStateToRouteKeywordFilter({ query }) {
            return query
        },
        getStateToRouteOrder ({ sortBy }) {
            return this.sortParamIndexMapping.find(({ index }) => index === sortBy)?.param
        },
        getStateToRouteCheckboxFilters ({ toggle }) {
            let checkboxFilters = Object.entries(toggle || {})

            return checkboxFilters.reduce((acc, [key, value]) => {
                key = this.getLabelFor(key)

                return { ...acc, [key]: [value] }
            }, {})
        },
        getStateToRouteMultiselectionFilters ({ refinementList }) {
            let multiselectionFilters = refinementList || {}

            return mapKeys(multiselectionFilters, (_, key) => this.getLabelFor(key))
        },
        getStateToRouteRangesFilters ({ range }) {
            let ranges = range || {}
                ranges = mapValues(ranges, value => [value.replace(':', '-')])
                ranges = mapKeys(ranges, (_, key) => this.getLabelFor(key))

            return ranges
        },
        getStateToRoutePage ({ page }) {
            return page
        },
        getStateToRouteMultiselectionTreeFilters ({ hierarchicalMenu }) {
            let multiselectionTreeFilters = hierarchicalMenu || {}
            multiselectionTreeFilters = mapKeys(hierarchicalMenu, (_, key) => key.replace(/.lvl0(\..+)?/, ''))

            return mapValues(multiselectionTreeFilters, values => [values.join('/')])
        },
        getRouteToStateQuery ({ search }) {
            return search?.keyword
        },
        getRouteToStateSortBy ({ order }) {
            return this.sortParamIndexMapping.find(({ param }) => param === order)?.index
        },
        getRouteToStateToggle ({ search }) {
            let toggle = Object.entries(search || {})
            toggle = toggle.filter(([attribute]) => this.toggleAttributes.includes(attribute))

            return toggle.reduce((acc, [key, value]) => {
                key = this.getFilterNameFor(key)

                return {
                    ...acc,
                    [key]: value[0]
                }
            }, {})
        },
        getRouteToStateRefinementList ({ search }) {
            let refinementList = Object.entries(search || {})
            refinementList = refinementList.filter(([attribute]) => this.multiselectionAttributes.includes(attribute))

            return refinementList.reduce((acc, [key, values]) => {
                key = this.getFilterNameFor(key)

                return {
                    ...acc,
                    [key]: values
                }
            }, {})
        },
        getRouteToStateRange ({ search }) {
            let range = Object.entries(search || {})
            range = range.filter(([attribute]) => this.rangeAttributes.includes(attribute))

            return range.reduce((acc, [key, values]) => {
                key = this.getFilterNameFor(key)

                return {
                    ...acc,
                    [key]: values[0].replace('-', ':')
                }
            }, {})
        },
        getRouteToStateHierarchicalMenu ({ search }) {
            let hierarchicalMenu = Object.entries(search || {})
            hierarchicalMenu = hierarchicalMenu.filter(([attribute]) => {
                return this.hierarchicalMenuAttributes.includes(attribute)
            })

            return hierarchicalMenu.reduce((acc, [key, values]) => {
                key = `${key}.lvl0`
                values = values.map(value => value.replace(/\//g, ' > '))

                return {
                    ...acc,
                    [key]: values
                }
            }, {})
        },
        getRouteToStatePage ({ page }) {
            return page
        },
        getAttributesFor (type) {
            return this.getFiltersFor(type).map(filter => this.getLabelFor(filter.name))
        },
        getFiltersFor (type) {
            return this.filters.filter(filter => filter.type === type)
        },
        getLabelFor (filterName) {
            filterName = filterName.replace(/^properties\.filter_/, '')

            return this.labelsMappings.find(mapping => mapping.filter === filterName)?.label || filterName
        },
        getFilterNameFor (label) {
            let filterName = this.labelsMappings.find(mapping => mapping.label === label)?.filter || label
            let foundFilter = this.filters.find(({ name }) => {
                return [filterName, `properties.filter_${filterName}`].includes(name)
            })

            return foundFilter?.name || filterName

        }
    }
}